import { useState, useEffect, useCallback } from "react";
import {
  fetchAndActivate,
  getValue,
  onConfigUpdate,
  activate,
} from "firebase/remote-config";
import { remoteConfig } from "../../config/firebase.config";
import {
  setAppLiveService,
  subscribeToWaitlistCollection,
} from "./waitlist.service";
import toast from "react-hot-toast";

export function useWaitlist() {
  const [showWaitlist, setShowWaitlist] = useState(true);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    // 1. Initial configuration fetch on mount
    async function initAppStatus() {
      try {
        await fetchAndActivate(remoteConfig);
      } catch (err) {
        console.error(
          "[useWaitlist] Remote Config initial fetch failed, using cached/default values:",
          err,
        );
      } finally {
        // Always read value (from cache/defaults if fetch failed)
        if (isMounted) {
          const isAppLive = getValue(remoteConfig, "is_app_live").asBoolean();
          setShowWaitlist(!isAppLive);
          setIsConfigLoading(false);
        }
      }
    }

    initAppStatus();

    // 2. Live updates via Firebase Remote Config SSE stream.
    //    Stream disconnects (network blip, tab backgrounded) are non-fatal —
    //    the SDK retries automatically.
    const unsubscribe = onConfigUpdate(remoteConfig, {
      next: (configUpdate) => {
        if (!isMounted) return;
        const updatedKeys = configUpdate.getUpdatedKeys();

        if (updatedKeys.has("is_app_live")) {
          activate(remoteConfig)
            .then(() => {
              if (!isMounted) return;
              const isAppLive = getValue(
                remoteConfig,
                "is_app_live",
              ).asBoolean();
              setShowWaitlist(!isAppLive);
            })
            .catch((err) => {
              console.error(
                "[useWaitlist] Failed to activate config after live push:",
                err,
              );
            });
        }
      },
      error: (error) => {
        if (error?.code === "remoteconfig/stream-error") {
          // Non-fatal: Firebase retries the stream automatically.
          console.warn(
            "[useWaitlist] Remote Config stream temporarily disconnected. Retrying automatically.",
          );
          return;
        }
        console.error("[useWaitlist] Config update streaming error:", error);
      },
    });

    return () => {
      isMounted = false;
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  const listenToWaitlist = useCallback(() => {
    setWaitlistLoading(true);
    const unsubscribe = subscribeToWaitlistCollection(
      (emailsData) => {
        setWaitlist(emailsData);
        setWaitlistLoading(false);
      },
      (error) => {
        console.error("[useWaitlist] Firestore waitlist stream crash:", error);
        toast.error("Failed to sync live waitlist emails.");
        setWaitlistLoading(false);
      },
    );

    return unsubscribe;
  }, []);

  const toggleWaitlist = useCallback(async (isLive) => {
    if (isLive === undefined || isLive === null) return;
    const toastId = "toggle-waitlist";
    try {
      setToggleLoading(true);
      toast.loading("Updating waitlist status...", { id: toastId });
      const response = await setAppLiveService(isLive);
      if (response?.status !== "SUCCESS") {
        throw new Error(
          response?.message ?? response?.error ?? "Failed to toggle waitlist.",
        );
      }
      toast.success(`Waitlist turned ${isLive ? "off" : "on"}`, {
        id: toastId,
      });
      return { success: true };
    } catch (error) {
      console.error("[useWaitlist] Toggle error:", error);
      toast.error(error?.message || "Failed to update waitlist status.", {
        id: toastId,
      });
      return { success: false };
    } finally {
      setToggleLoading(false);
    }
  }, []);

  return {
    showWaitlist,
    isConfigLoading,
    toggleWaitlist,
    toggleLoading,
    listenToWaitlist,
    waitlist,
    waitlistLoading,
  };
}
